<template>
  <div class="holdco-page">
    <div class="page-texture"></div>

    <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
      <div class="drawer-panel" @click.stop>
        <div class="drawer-header">
          <img src="../assets/CODE FORGE.png" alt="logo" class="drawer-logo" />
          <span class="drawer-brand">Code Forge</span>
          <button class="drawer-close" @click="drawer = false">✕</button>
        </div>
        <div class="drawer-divider"></div>
        <div class="drawer-nav">
          <a v-for="link in navLinks" :key="link.id" @click="scrollTo(link.id)" class="drawer-link">
            {{ link.text }}
          </a>
        </div>
      </div>
    </div>

    <div class="top-nav" :class="{ scrolled }">
      <div class="nav-container">
        <button class="nav-burger" @click="drawer = true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>

        <div class="nav-brand">
          <img src="../assets/CODE FORGE.png" alt="logo" class="nav-logo" />
          <div class="nav-brand-text">
            <span class="nav-brand-name">Code Forge</span>
            <span class="nav-brand-label">Holding Company</span>
          </div>
        </div>

        <div class="nav-links">
          <a
            v-for="link in navLinks"
            :key="link.id"
            :class="['nav-link', { active: activeSection === link.id }]"
            @click="scrollTo(link.id)"
          >
            {{ link.text }}
          </a>
        </div>
      </div>
    </div>

    <section class="hero-section">
      <div class="hero-container">
        <div class="hero-eyebrow">
          <span class="eyebrow-dot"></span>
          <span>Est. 2024 &mdash; A Holding Company</span>
        </div>

        <h1 class="hero-title">
          Software doesn't have<br />
          to be extractive.
        </h1>

        <p class="hero-subtitle">
          Code Forge Holdings builds and invests in software across categories —
          each product priced fairly, built to last, and operated by people who
          actually use it.
        </p>

        <div class="hero-buttons">
          <button class="btn-primary" @click="scrollTo('portfolio')">
            <span>View Our Portfolio</span>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="hero-ledger">
          <div class="ledger-item">
            <span class="ledger-value">07</span>
            <span class="ledger-label">Portfolio & Nonprofit</span>
          </div>
          <div class="ledger-divider"></div>
          <div class="ledger-item">
            <span class="ledger-value">2024</span>
            <span class="ledger-label">Founded</span>
          </div>
          <div class="ledger-divider"></div>
          <div class="ledger-item">
            <span class="ledger-value">100%</span>
            <span class="ledger-label">Independently Operated</span>
          </div>
        </div>
      </div>

      <div class="scroll-indicator" @click="scrollTo('about')">
        <div class="scroll-line"></div>
        <span>Scroll to explore</span>
      </div>
    </section>

    <section id="about" class="about-section reveal">
      <div class="section-container">
        <div class="about-grid">
          <div class="about-image-col">
            <div class="mark-frame">
              <img src="../assets/CODE FORGE.png" alt="Code Forge" class="mark-image" />
              <div class="mark-caption">Code Forge Holdings — Est. 2024</div>
            </div>
          </div>

          <div class="about-content-col">
            <div class="section-badge">Who We Are</div>
            <h2 class="section-title">One team, many products,<br />one set of principles.</h2>
            <p class="section-text">
              We're a small holding company that builds and invests in software
              across categories — not just one kind of product. We think software
              doesn't have to be extractive to be sustainable: it can be priced
              fairly, built well, and still work as a business. Every company we
              launch or back shares that same discipline underneath.
            </p>

            <div class="features-list">
              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3L3 8L12 13L21 8L12 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M3 16L12 21L21 16M3 12L12 17L21 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <h3>Fair, Not Extractive</h3>
                  <p>Honest, transparent pricing over hidden fees and lock-in — software that earns its price instead of extracting one.</p>
                </div>
              </div>

              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M12 8V12L15 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <h3>Operated, Not Just Owned</h3>
                  <p>We stay hands-on with every product long after launch — support, updates, and roadmap included.</p>
                </div>
              </div>

              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M13 3V10H19L11 21V14H5L13 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="feature-text">
                  <h3>Shared Engineering Discipline</h3>
                  <p>Every company we build draws on the same modern stack, security standards, and attention to detail.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="ceo" class="founder-section ceo-section reveal">
      <div class="section-container">
        <div class="founder-grid ceo-grid">
          <div class="founder-content-col">
            <div class="section-badge">Leadership</div>
            <h2 class="section-title">Chief Executive Officer</h2>
            <h3 class="founder-name">Christian Vernet</h3>
            <p class="founder-title">Chief Executive Officer · Code Forge Holdings</p>
            <p class="section-text founder-bio">
              Christian leads Code Forge Holdings with the kind of steady, people-first
              vision that makes an outsized company feel personal. He's an exceptional
              leader — sharp, decisive, and genuinely invested in the people and products
              he's responsible for — but what sets him apart is how easy he is to talk to.
              Whether it's a founder pitching a new idea or a teammate working through a
              tough problem, Christian brings the same warmth, patience, and clarity to
              every conversation. He has a gift for turning ambitious ideas into real,
              working businesses, and for building teams that trust him enough to do their
              best work. Under his leadership, Code Forge Holdings has grown into a
              portfolio of products that are as well-run as they are well-built.
            </p>

            <div class="founder-stats">
              <div class="founder-stat">
                <span class="founder-stat-value">07</span>
                <span class="founder-stat-label">Portfolio Companies Led</span>
              </div>
              <div class="founder-stat">
                <span class="founder-stat-value">100%</span>
                <span class="founder-stat-label">Hands-On Leadership</span>
              </div>
            </div>
          </div>

          <div class="founder-image-col">
            <div class="founder-frame">
              <img :src="ceoPortrait" alt="Christian Vernet" class="founder-portrait" />
              <div class="mark-caption">Chief Executive Officer</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="founder" class="founder-section reveal">
      <div class="section-container">
        <div class="founder-grid">
          <div class="founder-image-col">
            <div class="founder-frame">
              <img src="../assets/founder-portrait.png" alt="Enrico Michael Severe" class="founder-portrait" />
              <div class="founder-crown">♛</div>
              <div class="mark-caption">Founder &amp; President</div>
            </div>
          </div>

          <div class="founder-content-col">
            <div class="section-badge">Leadership</div>
            <h2 class="section-title">Founder &amp; President</h2>
            <h3 class="founder-name">Enrico Michael Severe</h3>
            <p class="founder-title">Private Researcher · Author · Founder · Economic Philosopher</p>
            <p class="founder-honorific">Davidic Heir · Son of King David</p>
            <p class="section-text founder-bio">
              Exploring the intersections of corporate power, systemic structures,
              and societal transformation through research, writing, and community
              action — grounded in the conviction that Yahuah's design for human
              flourishing is the foundation of every just economy.
            </p>

            <div class="founder-stats">
              <div class="founder-stat">
                <span class="founder-stat-value">24</span>
                <span class="founder-stat-label">Published Books</span>
              </div>
              <div class="founder-stat">
                <span class="founder-stat-value">3+</span>
                <span class="founder-stat-label">Research Articles</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="portfolio" class="portfolio-section reveal">
      <div class="section-container">
        <div class="section-header">
          <div class="section-badge">Our Portfolio</div>
          <h2 class="section-title">Some of Our Products in Our Portfolio</h2>
          <p class="section-subtitle">A look at some of the independent products — and one nonprofit — we build and invest in.</p>
        </div>

        <div class="portfolio-list">
          <a
            v-for="company in portfolio"
            :key="company.index"
            :href="company.url"
            target="_blank"
            rel="noopener"
            class="portfolio-row"
            :style="{ '--accent': company.color }"
          >
            <div class="portfolio-mark">
              <img v-if="company.logo" :src="company.logo" :alt="company.name" class="portfolio-logo" />
              <span v-else class="portfolio-index">{{ company.index }}</span>
            </div>

            <div class="portfolio-main">
              <div class="portfolio-heading">
                <h3 class="portfolio-name">{{ company.name }}</h3>
                <span class="portfolio-sector">{{ company.sector }}</span>
              </div>
              <p class="portfolio-description">{{ company.description }}</p>
            </div>

            <div class="portfolio-link">
              <span>{{ company.linkLabel }}</span>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M6 14L14 6M14 6H7M14 6V13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </a>
        </div>
      </div>
    </section>

    <footer class="footer-section">
      <div class="section-container">
        <div class="footer-grid">
          <div class="footer-brand">
            <img src="../assets/CODE FORGE.png" alt="logo" class="footer-logo" />
            <div class="brand-name">Code Forge Holdings</div>
            <p class="brand-tagline">A holding company that builds and invests in software — priced fairly, built to last.</p>
          </div>

          <div class="footer-links">
            <h4>Company</h4>
            <a @click="scrollTo('about')">About</a>
            <a @click="scrollTo('ceo')">CEO</a>
            <a @click="scrollTo('founder')">Founder</a>
            <a @click="scrollTo('portfolio')">Portfolio</a>
          </div>

          <div class="footer-links">
            <h4>Portfolio & Nonprofit</h4>
            <a href="https://schoolzconnect.com" target="_blank" rel="noopener">Schoolz Connect ↗</a>
            <a href="https://corporateconnectionz.com" target="_blank" rel="noopener">Corporate Connectionz ↗</a>
            <a href="https://heartlinehub.com" target="_blank" rel="noopener">Heartline Hub ↗</a>
            <a href="https://wrenzosuite.com" target="_blank" rel="noopener">Wrenzo Suite ↗</a>
            <a href="https://clublinksoftware.com" target="_blank" rel="noopener">ClubLink Software ↗</a>
            <a href="https://clubtrailz.com" target="_blank" rel="noopener">Club Trailz ↗</a>
            <a href="https://civilneighbr.com/login" target="_blank" rel="noopener">Civil Neighbr ↗</a>
          </div>
        </div>

        <div class="footer-bottom">
          <p>© {{ new Date().getFullYear() }} Code Forge Holdings. All rights reserved.</p>
        </div>
      </div>
    </footer>

    <button v-if="showTop" class="back-to-top" @click="scrollToTop">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

  </div>
</template>


<script>
import schoolzConnectLogo from '../assets/schoolz-connect.jpeg'
import corporateConnectionzLogo from '../assets/corporate-connectionz.png'
import heartlineHubLogo from '../assets/heartline-hub.png'
import wrenzoSuiteLogo from '../assets/wrenzo-suite.png'
import clublinkSoftwareLogo from '../assets/clublink-software.png'
import clubTrailzLogo from '../assets/club-trailz.png'
import civilNeighbrLogo from '../assets/civil-neighbr.png'
import ceoPortrait from '../assets/ceo-portrait.jpg'

  export default {
    name: 'HomeView',
    data() {
      return {
        drawer: false,
        activeSection: '',
        showTop: false,
        scrolled: false,
        ceoPortrait,

        navLinks: [
          { text: 'About', id: 'about' },
          { text: 'CEO', id: 'ceo' },
          { text: 'Founder', id: 'founder' },
          { text: 'Portfolio', id: 'portfolio' },
        ],
        portfolio: [
          {
            index: '01',
            name: 'Schoolz Connect',
            sector: 'K–12 Education',
            description: 'A social platform built for schools — students, teachers, parents, coaches, and staff share campus life, join classroom and club groups, and stay connected in one place.',
            url: 'https://schoolzconnect.com',
            linkLabel: 'schoolzconnect.com',
            color: '#D4A853',
            logo: schoolzConnectLogo
          },
          {
            index: '02',
            name: 'Corporate Connectionz',
            sector: 'Workplace & Culture',
            description: 'A social platform built for companies — employees and leadership share recognition and wins, join department and interest groups, and build culture across the organization.',
            url: 'https://corporateconnectionz.com',
            linkLabel: 'corporateconnectionz.com',
            color: '#6E9BC7',
            logo: corporateConnectionzLogo
          },
          {
            index: '03',
            name: 'Heartline Hub',
            sector: 'Senior Living',
            description: 'A social platform built for senior living communities — residents, families, and staff share moments, keep up with events and activities, and stay connected across the community.',
            url: 'https://heartlinehub.com',
            linkLabel: 'heartlinehub.com',
            color: '#C97C63',
            logo: heartlineHubLogo
          },
          {
            index: '04',
            name: 'Wrenzo Suite',
            sector: 'Auto Repair Shops',
            description: 'An all-in-one platform for auto shops — real-time scheduling, direct messaging, digital vehicle histories, and invoicing customers can pay from their phone, with automated reminders that keep them coming back.',
            url: 'https://wrenzosuite.com',
            linkLabel: 'wrenzosuite.com',
            color: '#E14B5A',
            logo: wrenzoSuiteLogo
          },
          {
            index: '05',
            name: 'ClubLink Software',
            sector: 'Private Club Management',
            description: 'A digital clubhouse for private clubs — member engagement, communication, and events, plus every operational tool a club needs (billing, tee times, dining, reciprocal member portal) at honest, transparent pricing.',
            url: 'https://clublinksoftware.com',
            linkLabel: 'clublinksoftware.com',
            color: '#7FA787',
            logo: clublinkSoftwareLogo
          },
          {
            index: '06',
            name: 'Club Trailz',
            sector: 'Reciprocal Club Travel',
            description: 'Travel intelligence for the reciprocal club world — members share where to eat, stay, and be looked after in any city, turning scattered word-of-mouth into an invite-only network clubs can act on.',
            url: 'https://clubtrailz.com',
            linkLabel: 'clubtrailz.com',
            color: '#9B84BE',
            logo: clubTrailzLogo
          },
          {
            index: '07',
            name: 'Civil Neighbr',
            sector: 'Nonprofit · Community Aid',
            description: 'A free platform connecting neighbors with local food, clothing, and support — organizations and individuals post real-time donations and requests, no bureaucracy required.',
            logo: civilNeighbrLogo,
            url: 'https://civilneighbr.com/login',
            linkLabel: 'civilneighbr.com',
            color: '#D9738C'
          },
        ],
      }
    },

    methods: {
      scrollTo(id) {
        const el = document.getElementById(id)
        if (el) {
          const offset = 80
          const elementPosition = el.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
        this.drawer = false
      },

      scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },

      setActiveOnScroll() {
        const ids = this.navLinks.map(n => n.id)
        const onScroll = () => {
          this.showTop = window.scrollY > 400
          this.scrolled = window.scrollY > 50

          let current = ''
          ids.forEach(id => {
            const el = document.getElementById(id)
            if (!el) return
            const rect = el.getBoundingClientRect()
            if (rect.top <= 150 && rect.bottom >= 150) {
              current = id
            }
          })
          this.activeSection = current
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
      },

      revealOnScroll() {
        const els = Array.from(document.querySelectorAll('.reveal'))
        const io = new IntersectionObserver(entries => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              e.target.classList.add('reveal-show')
            }
          })
        }, { threshold: 0.1 })
        els.forEach(el => io.observe(el))
      }
    },

    mounted() {
      this.setActiveOnScroll()
      this.revealOnScroll()
    }
  }
  </script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');

.holdco-page {
  --bg: #0A0D14;
  --bg-elevated: #0F131E;
  --panel: #12172380;
  --panel-solid: #131826;
  --hairline: rgba(230, 219, 194, 0.12);
  --hairline-strong: rgba(230, 219, 194, 0.24);
  --ink: #ECE6D8;
  --ink-dim: #A6ABB7;
  --ink-faint: #6E7480;
  --gold: #C6A15B;
  --gold-bright: #E3C078;
  --gold-dim: rgba(198, 161, 91, 0.14);

  position: relative;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  font-family: 'Inter', sans-serif;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}

.page-texture {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(rgba(230, 219, 194, 0.06) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%);
}

.section-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 32px;
  position: relative;
  z-index: 1;
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(4, 5, 8, 0.7);
  backdrop-filter: blur(4px);
  z-index: 200;
}

.drawer-panel {
  width: 300px;
  height: 100%;
  background: var(--bg-elevated);
  border-right: 1px solid var(--hairline-strong);
  padding: 28px 24px;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drawer-logo {
  width: 32px;
  height: 32px;
  border-radius: 4px;
}

.drawer-brand {
  font-family: 'Fraunces', serif;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
}

.drawer-close {
  background: none;
  border: none;
  color: var(--ink-dim);
  font-size: 16px;
  cursor: pointer;
}

.drawer-divider {
  height: 1px;
  background: var(--hairline);
  margin: 20px 0;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
}

.drawer-link {
  padding: 14px 0;
  color: var(--ink-dim);
  font-size: 15px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.drawer-link:hover {
  color: var(--gold-bright);
}

.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}

.top-nav.scrolled {
  background: rgba(10, 13, 20, 0.86);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--hairline);
}

.nav-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 20px 32px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-burger {
  display: none;
  background: none;
  border: none;
  color: var(--ink);
  cursor: pointer;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: auto;
}

.nav-logo {
  width: 34px;
  height: 34px;
  border-radius: 4px;
}

.nav-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.nav-brand-name {
  font-family: 'Fraunces', serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--ink);
}

.nav-brand-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gold);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 36px;
}

.nav-link {
  position: relative;
  font-size: 14px;
  color: var(--ink-dim);
  cursor: pointer;
  padding-bottom: 4px;
  transition: color 0.2s ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 1px;
  background: var(--gold);
  transition: width 0.25s ease;
}

.nav-link:hover,
.nav-link.active {
  color: var(--ink);
}

.nav-link:hover::after,
.nav-link.active::after {
  width: 100%;
}

.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 140px 24px 80px;
  text-align: center;
}

.hero-container {
  max-width: 780px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 32px;
}

.eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gold);
}

.hero-title {
  font-family: 'Fraunces', serif;
  font-size: clamp(38px, 5.6vw, 68px);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 26px;
}

.hero-subtitle {
  font-size: 17px;
  line-height: 1.65;
  color: var(--ink-dim);
  max-width: 540px;
  margin-bottom: 44px;
}

.hero-buttons {
  display: flex;
  gap: 16px;
  margin-bottom: 72px;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  background: var(--gold);
  border: 1px solid var(--gold);
  border-radius: 2px;
  color: #0A0D14;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-primary:hover {
  background: var(--gold-bright);
  border-color: var(--gold-bright);
  transform: translateY(-2px);
}

.btn-primary svg {
  transition: transform 0.25s ease;
}

.btn-primary:hover svg {
  transform: translateX(3px);
}

.btn-secondary {
  padding: 15px 30px;
  background: transparent;
  border: 1px solid var(--hairline-strong);
  border-radius: 2px;
  color: var(--ink);
  font-size: 14.5px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-secondary:hover {
  border-color: var(--gold);
  color: var(--gold-bright);
}

.hero-ledger {
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 40px;
  border-top: 1px solid var(--hairline);
  width: 100%;
  justify-content: center;
}

.ledger-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.ledger-value {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 600;
  color: var(--gold-bright);
}

.ledger-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.ledger-divider {
  width: 1px;
  height: 32px;
  background: var(--hairline-strong);
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: var(--ink-faint);
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.scroll-line {
  width: 1px;
  height: 34px;
  background: linear-gradient(to bottom, var(--gold), transparent);
}

.section-badge {
  display: inline-block;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--hairline-strong);
}

.section-title {
  font-family: 'Fraunces', serif;
  font-size: clamp(30px, 3.4vw, 42px);
  font-weight: 600;
  line-height: 1.15;
  color: var(--ink);
}

.section-subtitle {
  font-size: 16px;
  color: var(--ink-dim);
  margin-top: 14px;
  max-width: 520px;
}

.section-header {
  margin-bottom: 56px;
}

.section-text {
  font-size: 15.5px;
  line-height: 1.75;
  color: var(--ink-dim);
  margin: 22px 0 40px;
  max-width: 460px;
}

.about-section {
  padding: 140px 0;
  border-top: 1px solid var(--hairline);
}

.about-grid {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 80px;
  align-items: center;
}

.mark-frame {
  border: 1px solid var(--hairline-strong);
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.mark-image {
  width: 88px;
  height: 88px;
  border-radius: 6px;
  opacity: 0.94;
}

.mark-caption {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-faint);
  text-align: center;
}

.founder-section {
  padding: 140px 0;
  border-top: 1px solid var(--hairline);
}

.founder-grid {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 80px;
  align-items: center;
}

.ceo-section {
  border-top: none;
  padding-top: 0;
}

.ceo-grid {
  grid-template-columns: 1.2fr 0.8fr;
}

.founder-frame {
  border: 1px solid var(--hairline-strong);
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

.founder-portrait {
  width: 100%;
  max-width: 260px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  object-position: top center;
  border-radius: 4px;
  filter: grayscale(1);
  opacity: 0.96;
}

.founder-crown {
  font-size: 20px;
  color: var(--gold);
}

.founder-name {
  font-family: 'Fraunces', serif;
  font-size: 26px;
  font-weight: 600;
  color: var(--ink);
  margin-top: 18px;
  margin-bottom: 6px;
}

.founder-title {
  font-size: 14.5px;
  color: var(--ink-dim);
  margin-bottom: 8px;
}

.founder-honorific {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 22px;
}

.founder-bio {
  max-width: 520px;
}

.founder-stats {
  display: flex;
  gap: 48px;
  padding-top: 8px;
}

.founder-stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.founder-stat-value {
  font-family: 'Fraunces', serif;
  font-size: 30px;
  font-weight: 600;
  color: var(--gold-bright);
}

.founder-stat-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.features-list {
  display: flex;
  flex-direction: column;
}

.feature-item {
  display: flex;
  gap: 18px;
  padding: 22px 0;
  border-top: 1px solid var(--hairline);
}

.feature-item:last-child {
  border-bottom: 1px solid var(--hairline);
}

.feature-icon {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--hairline-strong);
  border-radius: 3px;
  color: var(--gold);
}

.feature-text h3 {
  font-size: 15.5px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 6px;
}

.feature-text p {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-dim);
}

.portfolio-section {
  padding: 100px 0 140px;
  border-top: 1px solid var(--hairline);
}

.portfolio-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--hairline-strong);
}

.portfolio-row {
  display: grid;
  grid-template-columns: 56px 1fr auto;
  align-items: center;
  gap: 32px;
  padding: 36px 8px;
  border-bottom: 1px solid var(--hairline-strong);
  text-decoration: none;
  color: inherit;
  position: relative;
  transition: padding 0.25s ease;
}

.portfolio-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
}

.portfolio-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
}

.portfolio-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--accent, var(--gold));
  transform: scaleY(0);
  transform-origin: center;
  transition: transform 0.25s ease;
}

.portfolio-row:hover::before {
  transform: scaleY(1);
}

.portfolio-row:hover {
  padding-left: 22px;
}

.portfolio-index {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 14px;
  color: var(--ink-faint);
  transition: color 0.2s ease;
}

.portfolio-row:hover .portfolio-index {
  color: var(--accent, var(--gold));
}

.portfolio-heading {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.portfolio-name {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 600;
  color: var(--ink);
}

.portfolio-sector {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent, var(--gold));
  padding: 4px 10px;
  border: 1px solid color-mix(in srgb, var(--accent, var(--gold)) 45%, transparent);
  border-radius: 20px;
}

.portfolio-description {
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--ink-dim);
  max-width: 560px;
}

.portfolio-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px;
  color: var(--ink-dim);
  white-space: nowrap;
  transition: color 0.2s ease;
}

.portfolio-row:hover .portfolio-link {
  color: var(--accent, var(--gold));
}


.footer-section {
  background: var(--bg-elevated);
  border-top: 1px solid var(--hairline-strong);
  padding: 64px 0 32px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  gap: 60px;
  margin-bottom: 48px;
}

.footer-logo {
  width: 38px;
  height: 38px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.brand-name {
  font-family: 'Fraunces', serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 10px;
}

.brand-tagline {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-faint);
  max-width: 280px;
}

.footer-links h4 {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-dim);
  margin-bottom: 18px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.footer-links a {
  display: block;
  font-size: 14.5px;
  color: var(--ink-faint);
  cursor: pointer;
  transition: color 0.2s ease;
  margin-bottom: 12px;
  text-decoration: none;
}

.footer-links a:hover {
  color: var(--gold-bright);
}

.footer-bottom {
  padding-top: 28px;
  border-top: 1px solid var(--hairline);
  text-align: left;
}

.footer-bottom p {
  font-size: 13px;
  color: var(--ink-faint);
}

.back-to-top {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--panel-solid);
  border: 1px solid var(--hairline-strong);
  border-radius: 50%;
  color: var(--gold);
  cursor: pointer;
  transition: all 0.25s ease;
  z-index: 100;
}

.back-to-top:hover {
  border-color: var(--gold);
  transform: translateY(-3px);
}

.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.7s ease;
}

.reveal-show {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 960px) {
  .nav-burger {
    display: flex;
  }

  .nav-links {
    display: none;
  }

  .hero-section {
    padding: 120px 24px 60px;
    min-height: auto;
  }

  .hero-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }

  .hero-ledger {
    flex-wrap: wrap;
    gap: 24px;
  }

  .about-grid,
  .founder-grid,
  .ceo-grid {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  .mark-frame,
  .founder-frame {
    padding: 32px;
  }

  .portfolio-row {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .portfolio-link {
    justify-content: flex-start;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .scroll-indicator {
    display: none;
  }
}
</style>